import { useState, useContext, useEffect } from 'react';
import { Card, Checkbox, Flex, Group, Pagination, ScrollArea } from '@mantine/core';
import { MenuMappingContext } from 'components/MenuManagement/MenuEdit/MenuMapping/MenuMapping';

import MenuMappingFilters from 'components/MenuManagement/MenuEdit/MenuMapping/MenuMappingFilters/MenuMappingFilters';
import InputText from 'primitives/InputText/InputText';
import { SearchIconSm } from '@chownow/pantry/icons';
import MappingTable from './MappingTable';
import MultiRowActions from './MultiRowActions';
import { Column, Item } from './types';

const PAGE_SIZE = 10;

interface PanelContentProps {
  columns: Column[];
  dataSource: Item[];
  filteredName: string;
  isModifierSelected?: boolean;
  setFilteredCategories: (categories: string[]) => void;
  setFilteredParents?: (parents: string[]) => void;
  setFilteredName: (name: string) => void;
  setFilteredStatuses: (statuses: string[]) => void;
}

export default function PanelContent({
  columns,
  dataSource,
  filteredName,
  isModifierSelected,
  setFilteredCategories,
  setFilteredName,
  setFilteredParents,
  setFilteredStatuses,
}: PanelContentProps) {
  const [page, setPage] = useState(1);

  const { checkedItems, setCheckedItems } = useContext(MenuMappingContext);
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);

  useEffect(() => {
    setCheckedItems([]);
  }, [page, setCheckedItems]);

  const firstIndex = (page - 1) * PAGE_SIZE;
  const lastIndex = page * PAGE_SIZE;

  const paginatedDataSource =
    dataSource.length <= PAGE_SIZE ? dataSource : dataSource.slice(firstIndex, lastIndex);

  const onSelectColumn = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsSelectAllChecked(e.target.checked);

    if (isSelectAllChecked) {
      setCheckedItems([]);
    } else {
      const checkedColumn: { [key: string]: boolean } = {};

      paginatedDataSource.forEach(item => {
        checkedColumn[item.key] = true;
      });

      setCheckedItems({
        ...checkedColumn,
        control: paginatedDataSource[0].key,
      });
    }
  };

  useEffect(() => {
    const hasUncheckedItems = Object.values(checkedItems).includes(false);
    if (isSelectAllChecked && (hasUncheckedItems || checkedItems.length === 0)) {
      setIsSelectAllChecked(false);
    }
  }, [checkedItems, isSelectAllChecked]);

  const selectColumn = {
    key: 'select',
    title: <Checkbox onChange={onSelectColumn} checked={isSelectAllChecked} />,
  };

  const tableColumns = [selectColumn, ...columns];

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement> | string) => {
    if (typeof e === 'string') {
      setFilteredName(e || '');
    } else if (!e.target?.value) {
      setFilteredName('');
    } else {
      setFilteredName(e.target.value);
    }
  };

  return (
    <Card mt="md" pr="0" mb="var(--dimension-xl5)">
      <Flex justify="space-between" mr="md" mb="md">
        <Group>
          <MenuMappingFilters
            isModifierSelected={isModifierSelected}
            setFilteredStatuses={setFilteredStatuses}
            setFilteredCategories={setFilteredCategories}
            setFilteredParents={setFilteredParents}
          />
          <MultiRowActions isModifierSelected={isModifierSelected} />
        </Group>
        <InputText
          size="sm"
          w="200"
          placeholder="Search name"
          leftIcon={<SearchIconSm />}
          value={filteredName}
          onChange={handleSearchChange}
        />
      </Flex>
      <ScrollArea>
        <MappingTable
          columns={tableColumns}
          dataSource={paginatedDataSource}
          isModifierSelected={isModifierSelected}
        />
      </ScrollArea>
      <Pagination
        mt="md"
        mr="md"
        total={Math.ceil(dataSource.length / PAGE_SIZE)}
        value={page}
        onChange={setPage}
      />
    </Card>
  );
}
